import React, { useEffect, useMemo, useState } from 'react';
import { Alert, AlertTitle, Box, Typography } from '@mui/material';
import { employmentApi } from '../../../services/api';
import { buildUpcomingSchedule } from '../../../utils/upcomingEmployeeEvents';

/**
 * ADR-136 phase 6 (§15 slice 3 refinement) — "Already scheduled": an advisory
 * panel for the end-date modals listing everything that has not fully happened
 * yet (Pending events, and Valid events whose effective date is still ahead),
 * so the operator sees the standing schedule before confirming a change to it.
 *
 * The list is fetched fresh when the modal opens — deliberately NOT derived
 * from the card's list payload, which can be stale (the incident that
 * motivated this panel: an end date added right after scheduling a
 * replacement, from a card that hadn't refetched).
 *
 * ADR-113 §8 — `episodeId` scopes the panel to the contract a CONTRACT verb
 * acts on. Without it the panel is PERSON-WIDE (ADR-185 D3 — the person verb
 * Resign): one group per open contract, labelled, primary first. With
 * `resignationDate` it also says what that resignation does to each scheduled
 * event (ADR-185 D4). What is listed and how it is worded lives in
 * utils/upcomingEmployeeEvents.js.
 *
 * Purely advisory: renders nothing while loading, on error, or when nothing is
 * scheduled — it must never block the modal it decorates.
 */
const NO_CONTRACTS = [];

const UpcomingEmployeeEvents = ({ employeeId, episodeId = null, contracts = NO_CONTRACTS, resignationDate = null }) => {
  const [episodes, setEpisodes] = useState([]);

  useEffect(() => {
    if (!employeeId) {
      return undefined;
    }
    let alive = true;

    (async () => {
      try {
        const fetched = await employmentApi.getEmploymentEpisodes(employeeId);
        if (alive) {
          setEpisodes(fetched || []);
        }
      } catch {
        // Advisory only — a failed lookup must never block the modal.
        if (alive) {
          setEpisodes([]);
        }
      }
    })();

    return () => { alive = false; };
  }, [employeeId]);

  const groups = useMemo(
    () => buildUpcomingSchedule({ episodes, episodeId, contracts, resignationDate }),
    [episodes, episodeId, contracts, resignationDate]
  );

  if (groups.length === 0) {
    return null;
  }

  // One group reads exactly as the panel always did; more get a heading each.
  const showHeadings = !episodeId && groups.length > 1;

  return (
    <Alert severity="warning">
      <AlertTitle>{episodeId ? 'Already scheduled on this contract' : 'Already scheduled for this person'}</AlertTitle>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {groups.map((group) => (
          <Box key={group.episodeId} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {showHeadings && (
              <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
                {group.label}{group.isPrimary ? ' (primary)' : ''}
              </Typography>
            )}
            {group.items.map((item) => (
              <Box key={item.id}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, ...(item.resolution?.startsWith('Cancelled') ? { textDecoration: 'line-through' } : {}) }}
                >
                  {item.primary}
                </Typography>
                {item.detail && (
                  <Typography variant="caption" sx={{ fontStyle: 'italic', display: 'block' }}>
                    {item.detail}
                  </Typography>
                )}
                {item.resolution && (
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                    {item.resolution}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
      <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
        {resignationDate && !episodeId
          ? 'Anything not marked above still happens automatically on its date unless changed or cancelled first.'
          : 'These happen automatically on their dates unless changed or cancelled first.'}
      </Typography>
    </Alert>
  );
};

export default UpcomingEmployeeEvents;
